import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { API_LINKS } from "@/constants/api";
import UserIcon from "@/components/icons/UserIcon";
import { getInitials } from "@/util/common";
import { UserInfo } from "@/interfaces/authentication";
import DelegationMenuItems from "@/components/delegation/DelegationMenuItems";
import { useDelegationContext } from "@/hooks/delegation/useDelegationContext";
import LogOutIcon from "./icons/AikaIcons/LogOutIcon";

export interface UserMenuProps {
  userName: string;
  subtitles?: string[];
  showAccountLink?: boolean;
  variant?: "default" | "guest";
  userInfo?: UserInfo;
}

const UserMenu = ({
  userName,
  subtitles = [],
  showAccountLink = false,
  variant = "default",
  userInfo,
}: UserMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: delegationContext } = useDelegationContext(Boolean(userInfo));

  const showDelegation = Boolean(
    userInfo &&
    (userInfo.isDelegatedSession ||
      (delegationContext?.availableLawyers?.length ?? 0) > 0),
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleSubtitles = subtitles.filter(Boolean);
  const closeMenu = () => setOpen(false);
  const showLogoutDivider = showAccountLink || showDelegation;

  return (
    <div className="dropdown position-relative user-menu" ref={ref}>
      <button
        type="button"
        id="user-menu-trigger"
        className="user-menu__trigger btn shadow-none bg-light p-2 btn-link  text-logo text-decoration-none d-flex gap-3 align-items-center p-0 border-0"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Brugermenu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="bg-white rounded-circle fw-bold text-logo d-flex align-items-center justify-content-center flex-shrink-0 user-menu__avatar">
          {variant === "guest" ? (
            <UserIcon height={18} />
          ) : (
            getInitials(userName)
          )}
        </span>
        <span className="d-none d-md-inline-flex flex-column align-items-start text-truncate user-menu__text">
          <span
            className="fw-bold lh-sm text-truncate user-menu__name"
            title={userName}
          >
            {userName || "Bruger"}
          </span>
          {visibleSubtitles.map((subtitle) => (
            <small
              key={subtitle}
              className="d-block text-muted fw-medium fs-7 lh-sm text-truncate"
              title={subtitle}
            >
              {subtitle}
            </small>
          ))}
        </span>
      </button>
      {open ? (
        <ul
          className="dropdown-menu dropdown-menu-end bg-white show position-absolute end-0 p-2 mt-1 shadow user-menu__dropdown"
          style={{ minWidth: "14rem" }}
          role="menu"
        >
          {showAccountLink ? (
            <li role="none">
              <Link
                id="user-menu-account"
                className="dropdown-item rounded-3 user-menu__item"
                to="/account"
                role="menuitem"
                onClick={closeMenu}
              >
                <span className="user-menu__item-icon">
                  <UserIcon />
                </span>
                <span className="user-menu__item-label">Mine oplysninger</span>
              </Link>
            </li>
          ) : null}
          {showAccountLink && showDelegation ? (
            <li role="none">
              <hr className="dropdown-divider my-1" />
            </li>
          ) : null}
          {userInfo && showDelegation ? (
            <DelegationMenuItems userInfo={userInfo} onClose={closeMenu} />
          ) : null}
          <li role="none" className="user-menu__logout">
            {showLogoutDivider ? (
              <hr className="dropdown-divider my-1" />
            ) : null}
            <a
              id="user-menu-logout"
              className="dropdown-item rounded-3 user-menu__item"
              href={API_LINKS.logout}
              role="menuitem"
            >
              <span className="user-menu__item-icon">
                <LogOutIcon />
              </span>
              <span className="user-menu__item-label">Log ud</span>
            </a>
          </li>
        </ul>
      ) : null}
    </div>
  );
};

export default UserMenu;
